import type { ServicePointCalibMetadata } from 'api/RobotApi'
import {
  getServiceCalibIconId,
  SERVICE_CALIB_ICON,
  ServiceCalibIconId,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibMapIcons'
import type { MaintenancePointKind, ServiceCalibCatalogItem } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibTypes'
import { KIND_MAINTENANCE_POINT } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibUtils'
import { MAINTENANCE_POINT_ALLOWED_ACTIONS } from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogItemActions'
import { IMaintenancePoint, MaintenancePointType } from 'utils/interfaces'
import { ACTION_TYPE_LABELS, ActionType } from 'utils/servicePointsCatalog'

export const MAINTENANCE_POINT_TYPE_OPTIONS: { value: MaintenancePointType; text: string }[] = [
  { value: 'waste_water_drain', text: 'Waste water drain' },
  { value: 'debris_dump', text: 'Debris dump' },
  { value: 'universal', text: 'Universal' },
]

export function maintenancePointTypeLabel(type: MaintenancePointType | string | undefined): string {
  return MAINTENANCE_POINT_TYPE_OPTIONS.find(option => option.value === type)?.text ?? type ?? '—'
}

export function maintenancePointAvailableActions(
  type: MaintenancePointType | string | undefined
): ActionType[] {
  if (!type || !(type in MAINTENANCE_POINT_ALLOWED_ACTIONS)) {
    return []
  }
  return [...(MAINTENANCE_POINT_ALLOWED_ACTIONS[type as MaintenancePointKind] ?? [])]
}

export function maintenancePointAvailableActionsLabel(type: MaintenancePointType | string | undefined): string {
  const actions = maintenancePointAvailableActions(type)
  if (!actions.length) {
    return '—'
  }
  return actions.map(action => ACTION_TYPE_LABELS[action] ?? String(action)).join(', ')
}

export function maintenancePointToCatalogItem(point: IMaintenancePoint): ServiceCalibCatalogItem | null {
  if (point.facility_index == null) {
    return null
  }
  const metadata: ServicePointCalibMetadata = {
    kind: KIND_MAINTENANCE_POINT,
    maintenance_point_id: point.facility_index,
  }
  return {
    key: `mp-${point.id}`,
    name: point.name,
    target: 'maintenance_point',
    point: point.point ?? null,
    metadata,
    maintenancePointId: point.facility_index,
    maintenancePointType: point.type as MaintenancePointKind,
  }
}

export function maintenancePointsToCatalogItems(points: IMaintenancePoint[]): ServiceCalibCatalogItem[] {
  return points
    .map(maintenancePointToCatalogItem)
    .filter((item): item is ServiceCalibCatalogItem => item != null && item.point?.coordinates?.length === 2)
}

export function maintenancePointMapIconId(type: MaintenancePointType | undefined): ServiceCalibIconId {
  if (!type) {
    return SERVICE_CALIB_ICON.MAINTENANCE_UNIVERSAL
  }
  const item: ServiceCalibCatalogItem = {
    key: 'preview',
    name: '',
    target: 'maintenance_point',
    point: null,
    metadata: { kind: KIND_MAINTENANCE_POINT, maintenance_point_id: 1 },
    maintenancePointType: type as MaintenancePointKind,
  }
  return getServiceCalibIconId(item)
}

export function parseMaintenancePointKey(key: string | null | undefined): number | undefined {
  if (!key?.startsWith('mp-')) {
    return undefined
  }
  const id = Number(key.slice(3))
  return Number.isFinite(id) ? id : undefined
}
